import { type ThemeState } from '@atlaskit/tokens';
import { GlobalPageLoadExperience } from '@atlassian/ufo';
import { type FrameIdentity } from '@databases/database-state/state/bridge-data';
import { setupGlobalLoadExperienceTimings } from '@databases/frontend-metrics/timings';
import { fetchIntlMessages } from '@databases/intl/fetch-intl-messages';
import { logFatalError } from '@databases/logs';
import { perf } from '@databases/logs/performance';
import { setAppMode, type AppMode } from '@databases/modes';
import { type ContainerOverrideOptions } from '@databases/service-container';

import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';
import { getDefaultLocale } from '@canvas/constants/localization/localization-types';
import { checkIndexeddbHealth } from '@canvas/indexeddb-state';
import { i18n } from '@canvas/intl';

import { addPolyfills } from './add-polyfills';
import { configureSentry } from './configure-sentry';
import { initializeAnalytics } from './initialize-analytics';
import { initializeCanvasListeners } from './initialize-canvas-listeners';
import { initializeConfluencePanelListeners } from './initialize-confluence-panel-listeners';
import { initializeDatabaseRovoSelection } from './initialize-database-rovo-selection';
import { initializeDatabaseTheme } from './initialize-database-theme';
import { initializeFeatureFlags } from './initialize-feature-flags';
import { initializeFieldTypesConfig } from './initialize-field-types-config';
import { initializeSentry } from './initialize-sentry';
import { initializeUserState } from './initialize-user-state';
import { registerCoreEventListeners } from './register-core-event-listeners';
import { registerDependenciesRequiredBeforeBootstrap } from './register-service-container-dependencies';

export interface BootstrapOptions {
  appMode: AppMode;
  cloudId: string;
  spaceId: string;
  identity: FrameIdentity;
  themeState?: Pick<ThemeState, 'light' | 'dark' | 'colorMode' | 'typography'>;
  /**
   * Optional dependency overrides for the service container.
   *
   * Note: Intl and SelectionsActions are not included here as they are injected separately.
   * Intl is injected during bootstrap, and SelectionsActions is registered via
   * initializeRecyclableSelectionsMachine after bootstrap completes.
   */
  dependencies: Omit<ContainerOverrideOptions, 'Intl' | 'SelectionsActions'>;
}

/**
 * Bootstraps the databases application. This initializes all the core dependencies needed to run the application.
 * This returns the `bootstrapApplication` method, which can only be invoked once this is to force app initialization
 * with Sentry first.
 */
export function initializeBootstrapping() {
  perf.time('app-initialization');

  initializeSentry();
  addPolyfills();

  const applyDependencyOverrides = registerDependenciesRequiredBeforeBootstrap();

  return async function bootstrapApplication({
    appMode,
    cloudId,
    dependencies,
    identity,
    spaceId,
    themeState,
  }: BootstrapOptions) {
    try {
      GlobalPageLoadExperience.startPageLoad('pending', true, performance.now());

      configureSentry({ cloudId, userId: identity.accountId });

      setAppMode(appMode);
      registerCoreEventListeners();

      const locale = identity.locale ?? getDefaultLocale();

      await Promise.all([
        fetchIntlMessages(locale),
        initializeFeatureFlags({ cloudId, userId: identity.accountId }),
      ]);

      applyDependencyOverrides({ ...dependencies, Intl: () => i18n.intl });

      initializeDatabaseTheme(themeState);
      initializeFieldTypesConfig({
        'databases-frontend-ci2-m1': isFeatureFlagEnabled('databases-frontend-ci2-m1'),
        'databases-frontend-live-doc': isFeatureFlagEnabled(
          'databases-frontend-live-doc'
        ),
      });
      initializeAnalytics({ cloudId, spaceId, accountId: identity.accountId });
      initializeConfluencePanelListeners();
      initializeCanvasListeners();
      initializeDatabaseRovoSelection();
      setupGlobalLoadExperienceTimings();
      initializeUserState({ identity });

      checkIndexeddbHealth().catch((e) => {
        console.error('Error initializing indexedDB health check:', e);
      });
    } catch (e) {
      if (e instanceof Error) {
        logFatalError(e);
      }

      throw e;
    }
  };
}
